
import { addressTitle } from '@shared/listing';
import { graphql, type ResultOf } from '@client/shared/graphql';
import { gqlRequest } from '@client/shared/graphql';

export const ListingDetailsQuery = graphql(`
  query ListingDetails($id: ID!) {
    listing(id: $id) {
      id
      formattedAddress
      addressLine1
      addressLine2
      city
      showingNotes
      timezone
      bookingHorizonDays
      bookingUntil
      bookingWindowEnd
      archivedAt
      viewerCanBook
      bookings {
        id
        startsAt
        endsAt
        agentUserId
      }
      unavailability {
        id
        startsAt
        endsAt
        reason
        createdAt
      }
    }
  }
`);

export type ListingDetailsData = ResultOf<typeof ListingDetailsQuery>;
export type ListingDetail = NonNullable<ListingDetailsData['listing']>;

export function listingDetailsKey(id: string) {
  return ['listing', 'details', id] as const;
}

export function fetchListingDetails(
  id: string,
  signal?: AbortSignal,
): Promise<ListingDetailsData> {
  return gqlRequest(ListingDetailsQuery, { id }, { signal });
}

export interface BookingEvent {
  readonly id: string;
  readonly start: string;
  readonly end: string;
  readonly agentUserId: string | null;
}

export interface UnavailabilityBlock {
  readonly id: string;
  readonly start: string;
  readonly end: string;
  readonly reason: string | null;
  readonly createdAt: string | null;
}

export function toBookingEvents(listing: ListingDetail | null | undefined): BookingEvent[] {
  const bookings = listing?.bookings ?? [];
  const events: BookingEvent[] = [];
  for (const b of bookings) {
    if (!b?.id || !b.startsAt || !b.endsAt) continue;
    events.push({ id: b.id, start: b.startsAt, end: b.endsAt, agentUserId: b.agentUserId ?? null });
  }
  return events;
}

export function toUnavailabilityBlocks(
  listing: ListingDetail | null | undefined,
): UnavailabilityBlock[] {
  const blocks = listing?.unavailability ?? [];
  const out: UnavailabilityBlock[] = [];
  for (const u of blocks) {
    if (!u?.id || !u.startsAt || !u.endsAt) continue;
    out.push({
      id: u.id,
      start: u.startsAt,
      end: u.endsAt,
      reason: u.reason ?? null,
      createdAt: u.createdAt ?? null,
    });
  }
  return out;
}

export function detailAddress(listing: ListingDetail): string {
  return addressTitle({
    formatted: listing.formattedAddress,
    line1: listing.addressLine1,
    line2: listing.addressLine2,
  });
}
